import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { Disclaimer } from "@/components/Shared";
import { WizardProgress, FieldGrid, WizardNav, validateStep, PortalHero } from "@/components/pathways/FormBits";
import { GraduationCap, AlertTriangle, CheckCircle2, ArrowRight, ShieldCheck, Clock, Briefcase, Trophy, Ban, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const YNU = ["Yes", "No", "Not sure"];
const YN = ["Yes", "No"];

const QUAL_LEVELS = ["Certificate", "Diploma", "Advanced Diploma", "Associate Degree", "Bachelor Degree", "Bachelor Honours", "Graduate Certificate", "Graduate Diploma", "Master by Coursework", "Master by Research", "Doctoral Degree"];
const HE = ["Bachelor Degree", "Bachelor Honours", "Master by Coursework", "Master by Research", "Doctoral Degree"];
const VOC = ["Certificate", "Diploma", "Advanced Diploma", "Associate Degree"];

const RISK_STATEMENTS = [
  "Are you relying entirely on receiving a skilled invitation?",
  "Have you NOT checked whether your occupation is eligible for the pathway you want?",
  "Are you assuming your employer will sponsor you without written confirmation?",
  "Are you currently working in an unrelated role?",
  "Do you expect to apply for another Student visa from inside Australia after the 485?",
  "Is your English result close to expiry?",
  "Is your skills assessment close to expiry?",
  "Will you reach an age threshold during your 485?",
  "Do you have fewer than 12 months remaining on your current visa?",
  "Do you have no backup option?",
  "Are you considering a new course mainly to remain in Australia?",
  "Are your work duties different from your proposed occupation?",
  "Are you being paid below the relevant market rate?",
  "Is your employer unable or unwilling to sponsor?",
  "Do you have limited evidence of relevant work experience?",
];

const STEP_TITLES = ["Current situation", "Course & study", "English", "Stream assessment", "Career position", "Skilled readiness", "Other pathways", "Time-use", "Risk check & consent"];

const STEP_WHY = [
  "The 485 has strict age, timing and visa-history requirements under the Migration Regulations. Your date of birth, current visa and history tell us whether you're within the age limit, inside the 6-month application window, and free of issues that need professional review first.",
  "Your qualification level and CRICOS study details determine which 485 stream you may fit — and whether you meet the Australian Study Requirement (at least 2 academic years / 92 weeks of registered study in Australia).",
  "English evidence is a time-of-application requirement for the 485. Your test type, date and scores are checked against the current legislated minimums — an expired or at-home test counts the same as no test.",
  "Each 485 stream has its own legal criteria. These targeted questions confirm the specific requirements of the stream your qualification points to (skills assessment for vocational graduates, regional evidence for a second 485, etc.).",
  "Your job doesn't affect 485 eligibility — the visa lets you work in any field. But it strongly shapes your options AFTER the 485: sponsorship, skills assessments and points all build from the work you do now.",
  "These are the exact factors Home Affairs points-tests for skilled visas (189/190/491). They tell us how ready you would be for a PR pathway once your 485 time starts running.",
  "Family, employer and regional circumstances can open alternative visa pathways that a points test alone won't reveal — we check the essential prerequisites, not just your preferences.",
  "This is the most important step: it shows the difference between simply holding a 485 and having a credible plan for it. Your answers directly shape your personalised action plan.",
  "These honest checks reveal the risks migration agents see cause problems every year. Ticking a statement never harms your result — it makes your report and warnings more accurate.",
];

function stepFields(idx, v) {
  switch (idx) {
    case 0: return [
      { key: "full_name", label: "Full name", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "phone", label: "Phone / WhatsApp", required: true },
      { key: "dob", label: "Date of birth", type: "date", required: true },
      { key: "nationality", label: "Nationality", required: true },
      { key: "current_country", label: "Current country", required: true },
      { key: "in_australia", label: "Currently in Australia?", type: "select", options: YN, required: true },
      { key: "current_visa", label: "Current visa subclass", placeholder: "e.g. Student 500" },
      { key: "visa_expiry", label: "Visa expiry date", type: "date" },
      { key: "prev_485", label: "Previously held a Subclass 485?", type: "select", options: YN, required: true },
      { key: "prev_replacement", label: "Previously held a replacement graduate visa?", type: "select", options: YN },
      { key: "refusal_history", label: "Any visa refusal or cancellation?", type: "select", options: YN, required: true },
      { key: "bridging", label: "Currently hold a Bridging visa?", type: "select", options: YN },
      { key: "family_included", label: "Family members included in your plans?", type: "select", options: YN },
    ];
    case 1: return [
      { key: "provider", label: "Education provider", required: true },
      { key: "course_name", label: "CRICOS course name" },
      { key: "course_code", label: "CRICOS course code" },
      { key: "qualification_level", label: "Qualification level", type: "select", options: QUAL_LEVELS, required: true },
      { key: "field_of_study", label: "Field of study", required: true },
      { key: "course_start", label: "Course commencement date", type: "date" },
      { key: "course_end", label: "Course completion date", type: "date" },
      { key: "completion_letter_date", label: "Date on completion letter", type: "date" },
      { key: "cricos_duration", label: "CRICOS-registered duration", placeholder: "e.g. 92 weeks" },
      { key: "actual_period", label: "Actual period studied in Australia", placeholder: "e.g. 2 years" },
      { key: "online_overseas", label: "Any study completed online or overseas?", type: "select", options: YNU },
      { key: "advanced_standing", label: "Received advanced standing / credit?", type: "select", options: YNU },
      { key: "combined_courses", label: "More than one course combined?", type: "select", options: YN },
      { key: "final_docs", label: "Final transcript & completion letter received?", type: "select", options: YN, required: true },
      { key: "regional_study", label: "Studied & lived in a designated regional area?", type: "select", options: YNU },
      { key: "regional_postcode", label: "Regional postcode (if applicable)" },
      { key: "still_regional", label: "Still living in the relevant regional area?", type: "select", options: YNU },
    ];
    case 2: return [
      { key: "passport_country", label: "Passport country", required: true },
      { key: "english_test_type", label: "English test type", type: "select", required: true,
        options: ["IELTS", "PTE Academic", "TOEFL iBT", "OET", "Cambridge C1", "Not taken yet"] },
      { key: "english_test_date", label: "Test date", type: "date" },
      { key: "test_at_centre", label: "Taken at an approved test centre?", type: "select", options: YNU },
      { key: "english_overall", label: "Overall score", type: "number" },
      { key: "english_lowest_component", label: "Lowest individual component score", type: "number" },
      { key: "combined_results", label: "More than one test result combined?", type: "select", options: YN },
      { key: "test_booked", label: "Do you have a test booked?", type: "select", options: YN },
      { key: "booked_date", label: "Booked test date (if any)", type: "date" },
    ];
    case 3: {
      const q = v.qualification_level;
      const f = [];
      if (HE.includes(q)) f.push(
        { key: "age_exception", label: "Is an age exception potentially relevant (research Masters / PhD)?", type: "select", options: YNU },
        { key: "indian_national", label: "Are you an Indian national?", type: "select", options: YN },
        { key: "hk_bno", label: "Hong Kong or BNO passport holder?", type: "select", options: YN },
      );
      if (VOC.includes(q)) f.push(
        { key: "proposed_occupation", label: "Proposed occupation", required: true },
        { key: "anzsco", label: "ANZSCO code (if known)" },
        { key: "occupation_eligible", label: "Is the occupation currently eligible for this stream?", type: "select", options: YNU },
        { key: "sa_authority", label: "Skills-assessment authority", placeholder: "e.g. TRA · VETASSESS" },
        { key: "sa_applied", label: "Skills assessment applied for?", type: "select", options: YN },
        { key: "sa_status_pv", label: "Skills assessment status", type: "select", options: ["Not started", "Application prepared", "Lodged", "Provisional", "Suitable", "Unsuccessful"] },
        { key: "qual_relates", label: "Qualification closely relates to the occupation?", type: "select", options: YNU },
        { key: "registration_required_pv", label: "Occupational registration required?", type: "select", options: YNU },
        { key: "registration_eligible", label: "Eligible for registration?", type: "select", options: YNU },
      );
      if (v.prev_485 === "Yes") f.push(
        { key: "first_485_eligible", label: "Held an eligible first 485 (Post-Higher Education / Post-Study Work)?", type: "select", options: YNU },
        { key: "institution_location", label: "Where was the qualifying institution located?", placeholder: "e.g. Wollongong (regional)" },
        { key: "lived_during_study", label: "Where did you live during study?" },
        { key: "lived_during_485", label: "Where have you lived while holding the first 485?" },
        { key: "second_regional_postcode", label: "Regional postcode" },
        { key: "regional_evidence", label: "Address evidence available", type: "multi",
          options: ["Lease", "Utility bills", "Bank statements", "Employment records", "Driver's licence", "Other address evidence"] },
      );
      if (f.length === 0) f.push({ key: "stream_notes", label: "Notes for our team about your study pathway", type: "textarea" });
      return f;
    }
    case 4: return [
      { key: "current_occupation", label: "Current occupation" },
      { key: "employment_status", label: "Employment status", type: "select", options: ["Full-time", "Part-time", "Casual", "Contractor", "Unemployed"], required: true },
      { key: "work_related", label: "Is the work related to your qualification?", type: "select", options: ["Yes", "Partially", "No", "Not working"], required: true },
      { key: "employer_name", label: "Employer name" },
      { key: "employment_start", label: "Employment commencement date", type: "date" },
      { key: "weekly_hours", label: "Average weekly hours", type: "number" },
      { key: "annual_salary", label: "Annual salary (AUD)" },
      { key: "has_contract", label: "Employment contract in place?", type: "select", options: YN },
      { key: "prof_registration_required", label: "Professional registration required?", type: "select", options: YNU },
      { key: "registration_obtained", label: "Registration obtained?", type: "select", options: ["Yes", "No", "In progress", "Not applicable"] },
      { key: "occupation_skilled_relevant", label: "Occupation relevant to a skilled migration pathway?", type: "select", options: YNU },
      { key: "sponsorship_discussed", label: "Discussed sponsorship with your employer?", type: "select", options: YN },
      { key: "employer_sponsored_before", label: "Has the employer sponsored workers before?", type: "select", options: YNU },
      { key: "sponsorship_response", label: "Sponsorship position", type: "select", required: true,
        options: ["Employer has confirmed willingness", "Employer may consider it", "Employer is unsure", "Employer has refused", "I have not discussed it", "I do not currently have an employer"] },
      { key: "au_experience", label: "Relevant Australian work experience?", type: "select", options: YN },
      { key: "overseas_experience", label: "Relevant overseas work experience?", type: "select", options: YN },
    ];
    case 5: return [
      { key: "skilled_occupation", label: "Proposed skilled occupation" },
      { key: "skills_assessment_status", label: "Skills assessment status", type: "select",
        options: ["Not started", "In progress", "Lodged", "Positive result in hand", "Unsuccessful"] },
      { key: "english_level", label: "Current English level", type: "select",
        options: ["Competent (IELTS 6)", "Proficient (IELTS 7)", "Superior (IELTS 8+)", "Below Competent", "Not tested"] },
      { key: "au_skilled_years", label: "Australian skilled work experience (years)", type: "number" },
      { key: "overseas_skilled_years", label: "Overseas skilled work experience (years)", type: "number" },
      { key: "study_requirement", label: "Australian study requirement met?", type: "select", options: YNU },
      { key: "regional_study_pts", label: "Regional study (points)?", type: "select", options: YN },
      { key: "community_language", label: "Community language credential (NAATI)?", type: "select", options: YN },
      { key: "professional_year", label: "Professional Year completed?", type: "select", options: YN },
      { key: "partner_status", label: "Partner status", type: "select",
        options: ["Single", "Partner with skills/English", "Partner is Australian citizen/PR", "Partner without skills evidence"] },
      { key: "state_preference", label: "State / territory preference" },
      { key: "relocate_regional", label: "Willing to relocate regionally?", type: "select", options: YNU },
      { key: "estimated_points", label: "Current estimated points (use our Points Calculator)", type: "number" },
      { key: "eoi_lodged", label: "EOI lodged?", type: "select", options: YN },
      { key: "roi_lodged", label: "State ROI lodged?", type: "select", options: YN },
      { key: "invitation_received", label: "Invitation received?", type: "select", options: YN },
    ];
    case 6: return [
      { key: "other_pathways", label: "Which of the following may be relevant to you?", type: "multi",
        options: ["Australian citizen or PR partner", "Eligible family relationship", "Employer sponsorship", "Regional employer", "Occupational training opportunity", "Further professional registration", "Research or doctoral pathway", "International work opportunity", "Intention to return home and use Australian qualification", "Unsure"] },
    ];
    case 7: return [
      { key: "completed_items", label: "What have you already completed?", type: "multi",
        options: ["Skills assessment", "Professional registration", "Higher English score", "Relevant employment", "Employment contract", "Sponsorship discussion", "EOI", "State nomination research", "Regional relocation research", "Document preparation", "Savings plan", "Backup pathway assessment", "None of these"] },
      { key: "priority", label: "Your expected priority during the 485", type: "select", required: true,
        options: ["Gain relevant skilled employment", "Build Australian work experience", "Improve English", "Complete registration", "Obtain a skills assessment", "Seek employer sponsorship", "Increase skilled migration points", "Move to a regional area", "Complete further study", "Earn income in any available employment", "Unsure"] },
    ];
    case 8: return [
      { key: "risk_flags", label: "Tick every statement that applies to you", type: "multi", options: RISK_STATEMENTS },
    ];
    default: return [];
  }
}

function urgentAlerts(v) {
  const a = [];
  const days = (d) => (new Date(d) - new Date()) / 86400000;
  if (v.visa_expiry && days(v.visa_expiry) <= 90) a.push("Your visa expires within 90 days — act urgently.");
  if (v.dob) {
    const age = (new Date() - new Date(v.dob)) / (365.25 * 86400000);
    if (age >= 35) a.push("You appear to be above the standard age limit of 35 — an exception review is required.");
    else if (age >= 33) a.push("You are approaching the 485 age limit of 35 — timing is critical.");
  }
  if (v.course_end && days(v.course_end) < -183) a.push("Course completion appears to be more than 6 months ago — the lodgement window may have closed.");
  if (v.prev_485 === "Yes") a.push("Previous 485 held — only the Second (regional) stream may remain available.");
  if (v.refusal_history === "Yes") a.push("Refusal / cancellation history declared — professional review required.");
  return a;
}

const ACTION_PLAN = [
  { period: "First 30 days", items: ["Confirm the correct skilled occupation", "Obtain an RMA review of the long-term pathway", "Check skills-assessment requirements", "Prepare professional CV and LinkedIn profile", "Check registration requirements", "Arrange the English test if improvement is needed"] },
  { period: "Months 2 – 3", items: ["Apply for skills assessment where suitable", "Actively seek qualification-related employment", "Begin documenting duties, hours, salary and supervision", "Discuss sponsorship carefully with suitable employers", "Research relevant state and regional pathways"] },
  { period: "Months 4 – 6", items: ["Review points", "Improve English where achievable", "Lodge an EOI or state registration where appropriate", "Consider regional employment", "Confirm employment duties align with the claimed occupation"] },
  { period: "Every 6 months", items: ["Repeat points and pathway assessment", "Check occupation-list and state criteria changes", "Review sponsorship feasibility", "Update work evidence", "Assess whether the main plan remains realistic"] },
  { period: "At least 12 months before expiry", items: ["Complete a formal backup pathway review", "Identify whether employer sponsorship is realistic", "Assess skilled nomination prospects", "Consider lawful offshore options where appropriate", "Prepare for departure if no further lawful visa pathway is available"] },
];

const VISA_GIVES = ["Temporary residence in Australia", "Unrestricted work rights", "Ability to work in any field", "Opportunity to obtain Australian experience", "Time to undertake skills assessment or registration", "Time to explore skilled or employer-sponsored pathways", "Possible second regional stream for eligible applicants", "Ability to include eligible family members, subject to the rules"];

const LIGHT_STYLE = {
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-rose-100 text-rose-800",
  grey: "bg-slate-100 text-slate-600",
};
const LIGHT_LABEL = { green: "Appears met", amber: "Evidence required", red: "Potential issue", grey: "Not assessed" };

const COMPLIANCE = "Important information: This online assessment provides general and preliminary information based solely on the answers supplied. It is not a final determination of visa eligibility, immigration assistance or legal advice, and it does not guarantee a visa, skilled invitation, employer sponsorship or permanent residency. Visa criteria and migration settings can change. A personalised assessment by a Registered Migration Agent or Australian legal practitioner is required before making or changing a visa, study, employment or migration decision.";

export default function Graduate485Planner() {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/g485-planner/rules`).then((r) => r.json()).then(setRules).catch(() => {});
  }, []);

  const fields = stepFields(step, values);
  const isLast = step === STEP_TITLES.length - 1;
  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const alerts = step <= 1 ? urgentAlerts(values) : [];

  const next = async () => {
    const missing = validateStep(fields, values);
    if (missing) { toast.error(`Please complete: ${missing}`); return; }
    if (!isLast) { setStep(step + 1); top(); return; }
    if (!consent) { toast.error("Please tick the consent checkbox to submit."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/g485-planner/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, consent: true, consent_timestamp: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setReport(data);
      toast.success("Your 485 Pathway Report is ready.");
      top();
    } catch (err) { toast.error(err.message || "Something went wrong — please try again."); }
    finally { setLoading(false); }
  };

  if (report) return <Report report={report} values={values} />;

  return (
    <div data-testid="g485-planner-page" className="bg-white">
      <PortalHero
        icon={GraduationCap}
        eyebrow="485 Visa & Future Pathway Check · Free"
        title="Check your 485 visa eligibility — and plan what comes after."
        subtitle="One free report with five results: your preliminary 485 eligibility · your likely stream · how long you can stay · your post-485 pathway readiness · a personalised action and risk plan."
      />
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-testid="g485-guide-link" className="mb-5 bg-white border border-[#29324A]/25 border-l-4 border-l-[#C43A89] p-4 flex items-start gap-3">
            <Info className="h-4 w-4 text-[#29324A] shrink-0 mt-0.5" />
            <p className="text-[13px] text-slate-700 leading-relaxed">
              <strong className="text-[#29324A]">New to the 485 visa?</strong> Read our complete{" "}
              <Link to="/temporary-graduate-485" className="text-[#C43A89] underline font-semibold">Temporary Graduate (485) visa guide</Link>{" "}
              first — streams, latest fees, eligibility and the most common refusal reasons — then come back and run your check.
            </p>
          </div>
          {rules?.last_reviewed && (
            <p className="text-[11px] text-slate-500 mb-4">Rules last reviewed: <strong>{rules.last_reviewed}</strong> · Source: {rules.source_authority}</p>
          )}
          {alerts.length > 0 && (
            <div data-testid="urgent-alerts" className="mb-5 bg-rose-50 border-l-4 border-rose-500 p-4 space-y-1.5">
              {alerts.map((a) => (
                <div key={a} className="flex gap-2 text-[13px] text-rose-900"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-rose-500" /> {a}</div>
              ))}
            </div>
          )}
          <div className="bg-white border border-slate-200 shadow-sm border-l-4 border-l-[#29324A] p-6 md:p-8">
            <WizardProgress titles={STEP_TITLES} current={step} />
            <div data-testid="step-why" className="mb-6 bg-[#F8F4FB] border-l-4 border-[#29324A] p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#29324A]">Why we ask this</div>
              <p className="text-[12.5px] text-slate-700 leading-relaxed mt-1">{STEP_WHY[step]}</p>
              <p className="text-[11px] text-slate-500 mt-2">Fields marked <strong className="text-[#C43A89]">*</strong> are required — everything else is optional but makes your report more accurate.</p>
            </div>
            <FieldGrid fields={fields} values={values} onChange={onChange} />
            {isLast && (
              <div className="mt-6">
                <p className="text-[11.5px] text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 p-4 mb-3">{COMPLIANCE}</p>
                <ConsentCheckbox value={consent} onChange={setConsent} testid="g485-planner-consent" />
              </div>
            )}
            <WizardNav onBack={() => { setStep(Math.max(0, step - 1)); top(); }} onNext={next}
              nextLabel={isLast ? "Generate My 485 Pathway Report" : "Continue"} loading={loading} showBack={step > 0} />
          </div>
        </div>
      </section>
      <Disclaimer />
    </div>
  );
}

function Sect({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-6" data-testid={testid}>
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89] flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5" />} {title}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Report({ report, values }) {
  return (
    <div data-testid="g485-planner-report" className="bg-[#F8F9FA]">
      <PortalHero icon={CheckCircle2} eyebrow={`Reference ${report.reference_id}`} title="Your 485 Graduate Pathway Report"
        subtitle="Based on the information provided, the following pathways may be suitable for professional assessment." />
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
          <p className="text-[11.5px] text-slate-500 leading-relaxed bg-white border border-slate-200 p-4">{COMPLIANCE}</p>

          {report.urgent_alerts?.length > 0 && (
            <div className="bg-rose-50 border-l-4 border-rose-500 p-4 space-y-1.5" data-testid="report-alerts">
              {report.urgent_alerts.map((a) => (
                <div key={a} className="flex gap-2 text-[13px] text-rose-900"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-rose-500" /> {a}</div>
              ))}
            </div>
          )}

          {/* 1. Preliminary result */}
          <Sect icon={GraduationCap} title="1 · Preliminary 485 result" testid="report-preliminary">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
              <div><span className="text-slate-500">Potential stream:</span> <strong className="text-[#29324A]">{report.stream_label}</strong></div>
              <div><span className="text-slate-500">Preliminary status:</span> <strong className="text-[#29324A]">{report.eligibility_status}</strong></div>
              <div><span className="text-slate-500">Estimated standard stay:</span> <strong>{report.expected_stay}</strong></div>
              <div><span className="text-slate-500">Current application charge:</span> <strong className="text-[#C43A89]">From AUD ${Number(report.application_fee).toLocaleString()}</strong></div>
              <div className="sm:col-span-2"><span className="text-slate-500">Professional review:</span> <strong>Required before application</strong></div>
            </div>
            <p className="text-[11.5px] text-slate-500 mt-3">Fees, requirements and processing arrangements can change. The amount shown is based on the system&apos;s last verified update. Rules last reviewed: {report.rules_last_reviewed}.</p>
          </Sect>

          {/* 2. Traffic lights */}
          <Sect icon={ShieldCheck} title="2 · Eligibility traffic-light overview" testid="report-lights">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[540px]">
                <thead><tr className="bg-[#29324A] text-white">
                  <th className="text-left px-3 py-2 font-display text-[12px]">Area</th>
                  <th className="text-left px-3 py-2 font-display text-[12px]">Status</th>
                  <th className="text-left px-3 py-2 font-display text-[12px]">Explanation</th>
                </tr></thead>
                <tbody>
                  {report.traffic_lights.map((l, i) => (
                    <tr key={l.area} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                      <td className="px-3 py-2.5 text-[12.5px] font-semibold text-[#29324A]">{l.area}</td>
                      <td className="px-3 py-2.5"><span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 ${LIGHT_STYLE[l.status]}`}>{LIGHT_LABEL[l.status]}</span></td>
                      <td className="px-3 py-2.5 text-[12.5px] text-slate-600">{l.explanation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Sect>

          {/* 3. Readiness */}
          <Sect icon={Trophy} title="3 · Your pathway readiness" testid="report-readiness">
            <div className="font-display text-2xl text-[#29324A]">Overall pathway readiness: <span className="text-[#C43A89]">{report.readiness_level}</span></div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-[10px] tracking-wider uppercase font-bold text-emerald-700 mb-2">Strengths</div>
                <ul className="space-y-1.5">{report.strengths.length ? report.strengths.map((s) => (
                  <li key={s} className="flex gap-2 text-[12.5px] text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {s}</li>
                )) : <li className="text-[12.5px] text-slate-500">No clear strengths identified yet.</li>}</ul>
              </div>
              <div>
                <div className="text-[10px] tracking-wider uppercase font-bold text-rose-700 mb-2">Current gaps</div>
                <ul className="space-y-1.5">{report.gaps.length ? report.gaps.map((g) => (
                  <li key={g} className="flex gap-2 text-[12.5px] text-slate-700"><Ban className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" /> {g}</li>
                )) : <li className="text-[12.5px] text-slate-500">No major gaps identified.</li>}</ul>
              </div>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-[13px]">
              <div className="bg-slate-50 border border-slate-200 p-3"><span className="text-slate-500">Time risk:</span> <strong className="text-[#29324A]">{report.time_risk}</strong></div>
              <div className="bg-slate-50 border border-slate-200 p-3"><span className="text-slate-500">Sponsorship readiness:</span> <strong className="text-[#29324A]">{report.sponsorship_readiness}</strong></div>
            </div>
          </Sect>

          {/* 4. What the 485 gives you */}
          <Sect icon={Briefcase} title="4 · What the 485 gives you" testid="report-gives">
            <div className="grid sm:grid-cols-2 gap-2">
              {VISA_GIVES.map((g) => (
                <div key={g} className="flex gap-2 text-[12.5px] text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {g}</div>
              ))}
            </div>
            <p className="mt-4 text-[12.5px] text-slate-700 bg-amber-50 border-l-4 border-amber-500 p-3">The visa does not provide a job, employer sponsorship, a skilled invitation or permanent residency. You are responsible for finding your own employment.</p>
          </Sect>

          {/* 5. Action plan */}
          <Sect icon={Clock} title="5 · Your personalised action plan" testid="report-plan">
            <div className="space-y-4">
              {ACTION_PLAN.map((b) => (
                <div key={b.period} className="border-l-2 border-[#29324A] pl-4">
                  <div className="font-display text-[15px] text-[#29324A]">{b.period}</div>
                  <ul className="mt-1.5 space-y-1">{b.items.map((i) => (
                    <li key={i} className="flex gap-2 text-[12.5px] text-slate-600"><ArrowRight className="h-3.5 w-3.5 text-[#C43A89] shrink-0 mt-0.5" /> {i}</li>
                  ))}</ul>
                </div>
              ))}
            </div>
          </Sect>

          {/* 6. Do nothing */}
          <Sect icon={AlertTriangle} title="6 · What happens if I do nothing?" testid="report-do-nothing">
            <p className="text-[13px] text-slate-700 mb-3">If your circumstances do not improve, the following risks may arise:</p>
            <ul className="space-y-1.5">{report.do_nothing_risks.map((r) => (
              <li key={r} className="flex gap-2 text-[12.5px] text-slate-700"><Ban className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" /> {r}</li>
            ))}</ul>
            {report.student_visa_warning && (
              <p className="mt-4 text-[12.5px] text-rose-900 bg-rose-50 border-l-4 border-rose-500 p-3" data-testid="student-visa-warning">{report.student_visa_warning}</p>
            )}
          </Sect>

          {/* 7. Career alignment */}
          <Sect icon={Briefcase} title="7 · Career-value check" testid="report-career">
            <div className="font-display text-xl text-[#29324A]">Career alignment: <span className="text-[#C43A89]">{report.career_alignment.level}</span></div>
            <p className="text-[13px] text-slate-700 leading-relaxed mt-2">{report.career_alignment.text}</p>
            <p className="text-[11.5px] text-slate-500 mt-2 italic">This is career and pathway guidance — not a legal requirement of your visa.</p>
          </Sect>

          {/* 8. Suggested pathways */}
          <Sect icon={Trophy} title="8 · Suggested pathway report" testid="report-pathways">
            {[["Primary pathway for professional assessment", report.pathways.primary],
              ["Alternative pathway", report.pathways.alternative],
              ["Backup pathway", report.pathways.backup],
              ["Exit or offshore plan", report.pathways.exit]].map(([label, pw]) => (
              <div key={label} className="mb-4 last:mb-0">
                <div className="text-[10px] tracking-wider uppercase font-bold text-[#29324A]">{label}</div>
                <div className="font-display text-[15px] text-[#29324A] mt-0.5">{pw.name}</div>
                <ul className="mt-1 space-y-1">{pw.reasons.map((r) => (
                  <li key={r} className="flex gap-2 text-[12.5px] text-slate-600"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" /> {r}</li>
                ))}</ul>
              </div>
            ))}
          </Sect>

          {/* 9. Lead conversion */}
          <div className="bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white p-6 md:p-8" data-testid="report-ctas">
            <div className="font-display text-2xl">What would you like to do next?</div>
            <p className="text-[13px] text-slate-200 mt-2">A confirmation email with reference <strong>{report.reference_id}</strong> has been sent to {values.email}.</p>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              <Link to="/contact" data-testid="cta-course-review" className="inline-flex items-center justify-center gap-2 bg-white text-[#29324A] hover:bg-[#C43A89] hover:text-white text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-3.5 text-center transition-colors">Get My Free Course &amp; Career Review</Link>
              <Link to="/book-consultation" data-testid="cta-consultation" className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-3.5 text-center transition-colors">Book a 485 &amp; PR Pathway Consultation</Link>
              <Link to="/book-consultation" data-testid="cta-rma-report" className="inline-flex items-center justify-center gap-2 border border-white/40 hover:bg-white/10 text-white text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-3.5 text-center transition-colors">Request a Full RMA-Reviewed Pathway Report</Link>
            </div>
          </div>

          <p className="text-[11px] text-slate-500 flex items-center gap-2"><Info className="h-3.5 w-3.5" /> Rules last reviewed: {report.rules_last_reviewed} · Source: {report.rules_source} · <Link to="/temporary-graduate-485" className="text-[#C43A89] underline font-semibold">Read the full 485 visa guide</Link></p>
        </div>
      </section>
      <Disclaimer />
    </div>
  );
}
